import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";

function generateBasePath(): string {
  const base = __BASE_PATH__;
  const parts = base.split('/').filter(Boolean);
  return parts.length > 0 ? '/' + parts.join('/') : '';
}

const pathPrefix = generateBasePath();

type Tab = "login" | "signup";

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading, isAdmin, roleLoading, sessionConflict, pendingBlocked, signIn, signUp, resetPassword } = useAuth();

  const urlTab = searchParams.get("tab");
  const urlRef = searchParams.get("ref");

  const [tab, setTab] = useState<Tab>(urlTab === "signup" ? "signup" : "login");
  const [showPw, setShowPw] = useState(false);
  const [showPwConfirm, setShowPwConfirm] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [referralCode, setReferralCode] = useState(urlRef ? urlRef.toUpperCase() : "");
  const [referralAgency, setReferralAgency] = useState<string | null>(null);
  const [referralValid, setReferralValid] = useState<"valid" | "invalid" | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: "success" | "error" | "info" } | null>(null);
  const [showResetPw, setShowResetPw] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!authLoading && !roleLoading && user) {
      const isMobile = window.innerWidth < 768;
      navigate(isAdmin && !isMobile ? "/admin" : "/dashboard");
    }
  }, [user, authLoading, roleLoading, isAdmin, navigate]);

  // 중복 로그인 감지 시 경고 메시지 표시
  useEffect(() => {
    if (sessionConflict) {
      setToast({ msg: "다른 기기에서 로그인되어 현재 세션이 종료되었습니다. 다시 로그인해주세요.", type: "error" });
      setTimeout(() => setToast(null), 5000);
    }
  }, [sessionConflict]);

  // 관리자 승인 대기 상태 감지
  useEffect(() => {
    if (pendingBlocked) {
      setToast({ msg: "관리자 승인 대기 중입니다. 승인 완료 후 로그인이 가능합니다.", type: "info" });
      setTimeout(() => setToast(null), 6000);
    }
  }, [pendingBlocked]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
    const particles: { x: number; y: number; vx: number; vy: number; size: number; opacity: number }[] = [];
    for (let i = 0; i < 40; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        size: Math.random() * 1.5 + 0.5,
        opacity: Math.random() * 0.4 + 0.1,
      });
    }
    let animId: number;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(167, 139, 250, ${p.opacity})`;
        ctx.fill();
      });
      animId = requestAnimationFrame(animate);
    };
    animate();
    return () => cancelAnimationFrame(animId);
  }, []);

  useEffect(() => {
    if (urlRef && tab === "signup") {
      validateReferralCode(urlRef.toUpperCase());
    }
  }, []);

  useEffect(() => {
    if (urlRef && tab === "signup" && !referralCode) {
      setReferralCode(urlRef.toUpperCase());
    }
  }, [tab, urlRef]);

  const showToast = (msg: string, type: "success" | "error" | "info") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      showToast("이메일과 비밀번호를 입력해주세요.", "error");
      return;
    }
    setLoading(true);
    const { error } = await signIn(email, password);
    setLoading(false);
    if (error) {
      if (error.message.includes("Invalid login credentials")) {
        showToast("이메일 또는 비밀번호가 올바르지 않습니다.", "error");
      } else if (error.message.includes("Email not confirmed")) {
        showToast("이메일 인증이 필요합니다. 받은 메일함을 확인해주세요.", "error");
      } else {
        showToast(error.message, "error");
      }
      return;
    }
    showToast("로그인 성공! 환영합니다.", "success");
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !email || !phone || !password || !passwordConfirm) {
      showToast("모든 항목을 입력해주세요.", "error");
      return;
    }
    if (password.length < 8) {
      showToast("비밀번호는 8자 이상이어야 합니다.", "error");
      return;
    }
    if (password !== passwordConfirm) {
      showToast("비밀번호가 일치하지 않습니다.", "error");
      return;
    }
    if (!agreed) {
      showToast("이용약관에 동의해주세요.", "error");
      return;
    }
    setLoading(true);
    const { data, error } = await signUp(email, password, { name, phone, referralCode });
    setLoading(false);
    if (error) {
      if (error.message.includes("already registered") || error.message.includes("User already registered")) {
        showToast("이미 가입된 이메일입니다. 로그인해주세요.", "error");
      } else {
        showToast(error.message, "error");
      }
      return;
    }
    if (data.user && !data.session) {
      showToast("가입 신청이 완료되었습니다. 관리자 승인 후 로그인이 가능합니다.", "success");
      setTab("login");
    } else {
      showToast("회원가입 완료! 관리자 승인 후 이용 가능합니다.", "success");
      setTab("login");
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetEmail) {
      showToast("이메일을 입력해주세요.", "error");
      return;
    }
    setLoading(true);
    const { error } = await resetPassword(resetEmail);
    setLoading(false);
    if (error) {
      showToast(error.message, "error");
      return;
    }
    showToast("비밀번호 재설정 링크를 이메일로 발송했습니다.", "success");
    setShowResetPw(false);
    setResetEmail("");
  };

  const validateReferralCode = async (code: string) => {
    if (!code.trim()) {
      setReferralAgency(null);
      setReferralValid(null);
      return;
    }
    try {
      const res = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/get-agency-by-code`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string,
          Authorization: `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string}`,
        },
        body: JSON.stringify({ code }),
      });
      if (res.ok) {
        const agencyData = await res.json();
        setReferralAgency(agencyData.full_name);
        setReferralValid("valid");
      } else {
        setReferralAgency(null);
        setReferralValid("invalid");
      }
    } catch {
      setReferralAgency(null);
      setReferralValid("invalid");
    }
  };

  const switchTab = (t: Tab) => {
    setTab(t);
    setEmail("");
    setPassword("");
    setPasswordConfirm("");
    setName("");
    setPhone("");
    setReferralCode("");
    setReferralAgency(null);
    setReferralValid(null);
    setAgreed(false);
    setShowPw(false);
    setShowPwConfirm(false);
  };

  if (authLoading || roleLoading) {
    return (
      <div className="min-h-screen bg-[#0a0a18] flex items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-violet-400 text-3xl"></i>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a18] flex items-center justify-center relative overflow-hidden px-4 py-10">
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" />
      <div className="absolute top-1/4 left-1/4 w-80 h-80 bg-violet-600/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/3 right-1/4 w-72 h-72 bg-pink-600/15 rounded-full blur-3xl pointer-events-none" />

      {/* Toast */}
      {toast && (
        <div
          className={`fixed top-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-xl text-sm font-medium flex items-center gap-2 transition-all whitespace-nowrap ${
            toast.type === "success"
              ? "bg-green-500/20 border border-green-500/30 text-green-300"
              : toast.type === "info"
              ? "bg-violet-500/20 border border-violet-500/30 text-violet-300"
              : "bg-red-500/20 border border-red-500/30 text-red-300"
          }`}
        >
          <i
            className={`text-base ${
              toast.type === "success"
                ? "ri-checkbox-circle-line"
                : toast.type === "info"
                ? "ri-information-line"
                : "ri-error-warning-line"
            }`}
          ></i>
          {toast.msg}
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPw && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-[#13131f] border border-white/[0.1] rounded-2xl p-7 w-full max-w-sm">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-white font-semibold text-base">비밀번호 찾기</h3>
              <button
                onClick={() => setShowResetPw(false)}
                className="w-7 h-7 flex items-center justify-center text-gray-500 hover:text-white cursor-pointer transition-colors"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <p className="text-gray-400 text-xs mb-5 leading-relaxed">
              가입하신 이메일 주소를 입력하시면 비밀번호 재설정 링크를 보내드립니다.
            </p>
            <form onSubmit={handleResetPassword} className="flex flex-col gap-4">
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                  <i className="ri-mail-line text-gray-500 text-sm"></i>
                </div>
                <input
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  placeholder="example@email.com"
                  className="w-full bg-white/[0.05] border border-white/[0.1] rounded-lg pl-9 pr-4 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-violet-500/60 transition-all"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-500 hover:to-pink-500 disabled:opacity-60 text-white py-2.5 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                {loading ? (
                  <><i className="ri-loader-4-line animate-spin text-base"></i>발송 중...</>
                ) : (
                  "재설정 링크 발송"
                )}
              </button>
            </form>
          </div>
        </div>
      )}

      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 cursor-pointer">
            <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-pink-500 text-white font-bold text-base">
              S
            </div>
            <span className="text-white font-semibold text-lg whitespace-nowrap">Signal SMS</span>
          </Link>
          <p className="text-gray-500 text-sm mt-3">
            {tab === "login" ? "계정에 로그인하세요" : "새 계정을 만들어보세요"}
          </p>
        </div>

        {/* Card */}
        <div className="bg-white/[0.04] border border-white/[0.08] rounded-2xl p-8 backdrop-blur-sm">
          {/* Tab */}
          <div className="flex bg-white/[0.05] rounded-xl p-1 mb-7">
            <button
              onClick={() => switchTab("login")}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                tab === "login"
                  ? "bg-gradient-to-r from-violet-600 to-pink-600 text-white"
                  : "text-gray-400 hover:text-white"
              }`}
            >
              로그인
            </button>
            <button
              onClick={() => switchTab("signup")}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                tab === "signup"
                  ? "bg-gradient-to-r from-violet-600 to-pink-600 text-white"
                  : "text-gray-400 hover:text-white"
              }`}
            >
              회원가입
            </button>
          </div>

          {/* Login Form */}
          {tab === "login" && (
            <form onSubmit={handleLogin} className="flex flex-col gap-4">
              <div>
                <label className="block text-gray-400 text-xs mb-1.5 font-medium">이메일</label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                    <i className="ri-mail-line text-gray-500 text-sm"></i>
                  </div>
                  <input
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="example@email.com"
                    autoComplete="email"
                    className="w-full bg-white/[0.05] border border-white/[0.1] rounded-lg pl-9 pr-4 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-violet-500/60 focus:bg-white/[0.07] transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-400 text-xs mb-1.5 font-medium">비밀번호</label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                    <i className="ri-lock-line text-gray-500 text-sm"></i>
                  </div>
                  <input
                    type={showPw ? "text" : "password"}
                    name="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="비밀번호를 입력하세요"
                    autoComplete="current-password"
                    className="w-full bg-white/[0.05] border border-white/[0.1] rounded-lg pl-9 pr-10 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-violet-500/60 focus:bg-white/[0.07] transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw(!showPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center cursor-pointer"
                  >
                    <i className={`${showPw ? "ri-eye-off-line" : "ri-eye-line"} text-gray-500 text-sm`}></i>
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" className="w-3.5 h-3.5 accent-violet-500 cursor-pointer" />
                  <span className="text-gray-400 text-xs">로그인 상태 유지</span>
                </label>
                <button
                  type="button"
                  onClick={() => setShowResetPw(true)}
                  className="text-violet-400 text-xs hover:text-violet-300 transition-colors cursor-pointer whitespace-nowrap"
                >
                  비밀번호 찾기
                </button>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-500 hover:to-pink-500 disabled:opacity-60 text-white py-3 rounded-lg text-sm font-semibold transition-all hover:scale-[1.02] cursor-pointer whitespace-nowrap mt-1 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <><i className="ri-loader-4-line animate-spin text-base"></i>로그인 중...</>
                ) : (
                  "로그인"
                )}
              </button>

              <div className="relative flex items-center gap-3 my-1">
                <div className="flex-1 h-px bg-white/[0.08]"></div>
                <span className="text-gray-600 text-xs">또는</span>
                <div className="flex-1 h-px bg-white/[0.08]"></div>
              </div>

              <button
                type="button"
                className="w-full bg-white/[0.05] hover:bg-white/[0.08] border border-white/[0.1] text-white py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                onClick={() => showToast("카카오 로그인은 준비 중입니다.", "info")}
              >
                <i className="ri-kakao-talk-fill text-yellow-400 text-base"></i>
                카카오로 시작하기
              </button>
            </form>
          )}

          {/* Signup Form */}
          {tab === "signup" && (
            <form onSubmit={handleSignup} className="flex flex-col gap-4">
              <div>
                <label className="block text-gray-400 text-xs mb-1.5 font-medium">이름 / 회사명</label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                    <i className="ri-user-line text-gray-500 text-sm"></i>
                  </div>
                  <input
                    type="text"
                    name="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="홍길동 또는 (주)회사명"
                    autoComplete="name"
                    className="w-full bg-white/[0.05] border border-white/[0.1] rounded-lg pl-9 pr-4 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-violet-500/60 focus:bg-white/[0.07] transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-400 text-xs mb-1.5 font-medium">이메일</label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                    <i className="ri-mail-line text-gray-500 text-sm"></i>
                  </div>
                  <input
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="example@email.com"
                    autoComplete="email"
                    className="w-full bg-white/[0.05] border border-white/[0.1] rounded-lg pl-9 pr-4 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-violet-500/60 focus:bg-white/[0.07] transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-400 text-xs mb-1.5 font-medium">휴대폰 번호</label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                    <i className="ri-smartphone-line text-gray-500 text-sm"></i>
                  </div>
                  <input
                    type="tel"
                    name="phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="010-0000-0000"
                    autoComplete="tel"
                    className="w-full bg-white/[0.05] border border-white/[0.1] rounded-lg pl-9 pr-4 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-violet-500/60 focus:bg-white/[0.07] transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-400 text-xs mb-1.5 font-medium">비밀번호</label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                    <i className="ri-lock-line text-gray-500 text-sm"></i>
                  </div>
                  <input
                    type={showPw ? "text" : "password"}
                    name="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="8자 이상 입력하세요"
                    autoComplete="new-password"
                    className="w-full bg-white/[0.05] border border-white/[0.1] rounded-lg pl-9 pr-10 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-violet-500/60 focus:bg-white/[0.07] transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw(!showPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center cursor-pointer"
                  >
                    <i className={`${showPw ? "ri-eye-off-line" : "ri-eye-line"} text-gray-500 text-sm`}></i>
                  </button>
                </div>
                {password && password.length < 8 && (
                  <p className="text-amber-400 text-xs mt-1">비밀번호는 8자 이상이어야 합니다.</p>
                )}
              </div>

              <div>
                <label className="block text-gray-400 text-xs mb-1.5 font-medium">비밀번호 확인</label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                    <i className="ri-lock-2-line text-gray-500 text-sm"></i>
                  </div>
                  <input
                    type={showPwConfirm ? "text" : "password"}
                    name="passwordConfirm"
                    value={passwordConfirm}
                    onChange={(e) => setPasswordConfirm(e.target.value)}
                    placeholder="비밀번호를 다시 입력하세요"
                    autoComplete="new-password"
                    className={`w-full bg-white/[0.05] border rounded-lg pl-9 pr-10 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none transition-all ${
                      passwordConfirm && password !== passwordConfirm
                        ? "border-red-500/50 focus:border-red-500/70"
                        : passwordConfirm && password === passwordConfirm
                        ? "border-green-500/50 focus:border-green-500/70"
                        : "border-white/[0.1] focus:border-violet-500/60 focus:bg-white/[0.07]"
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwConfirm(!showPwConfirm)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center cursor-pointer"
                  >
                    <i className={`${showPwConfirm ? "ri-eye-off-line" : "ri-eye-line"} text-gray-500 text-sm`}></i>
                  </button>
                </div>
                {passwordConfirm && password !== passwordConfirm && (
                  <p className="text-red-400 text-xs mt-1">비밀번호가 일치하지 않습니다.</p>
                )}
                {passwordConfirm && password === passwordConfirm && password.length >= 8 && (
                  <p className="text-green-400 text-xs mt-1 flex items-center gap-1">
                    <i className="ri-checkbox-circle-line"></i>비밀번호가 일치합니다.
                  </p>
                )}
              </div>

              {/* 추천인 코드 */}
              <div>
                <label className="block text-gray-400 text-xs mb-1.5 font-medium">
                  추천인 코드 <span className="text-gray-600">(선택)</span>
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                    <i className="ri-gift-line text-gray-500 text-sm"></i>
                  </div>
                  <input
                    type="text"
                    name="referralCode"
                    value={referralCode}
                    onChange={(e) => {
                      setReferralCode(e.target.value.toUpperCase());
                      setReferralValid(null);
                      setReferralAgency(null);
                    }}
                    onBlur={() => validateReferralCode(referralCode)}
                    placeholder="에이전시 추천인 코드 입력"
                    className={`w-full bg-white/[0.05] border rounded-lg pl-9 pr-4 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none transition-all uppercase tracking-widest ${
                      referralValid === "valid"
                        ? "border-green-500/50 focus:border-green-500/70"
                        : referralValid === "invalid"
                        ? "border-red-500/50 focus:border-red-500/70"
                        : "border-white/[0.1] focus:border-violet-500/60 focus:bg-white/[0.07]"
                    }`}
                  />
                </div>
                {referralValid === "valid" && referralAgency && (
                  <p className="text-green-400 text-xs mt-1 flex items-center gap-1">
                    <i className="ri-checkbox-circle-line"></i>
                    <span className="font-medium">{referralAgency}</span> 에이전시와 연결됩니다
                  </p>
                )}
                {referralValid === "invalid" && (
                  <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
                    <i className="ri-error-warning-line"></i>
                    유효하지 않은 추천인 코드입니다.
                  </p>
                )}
              </div>

              <label className="flex items-start gap-2.5 cursor-pointer mt-1">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="w-3.5 h-3.5 accent-violet-500 cursor-pointer mt-0.5 shrink-0"
                />
                <span className="text-gray-400 text-xs leading-relaxed">
                  <span className="text-violet-400 hover:text-violet-300 cursor-pointer">이용약관</span> 및{" "}
                  <span className="text-violet-400 hover:text-violet-300 cursor-pointer">개인정보처리방침</span>에 동의합니다.
                </span>
              </label>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-500 hover:to-pink-500 disabled:opacity-60 text-white py-3 rounded-lg text-sm font-semibold transition-all hover:scale-[1.02] cursor-pointer whitespace-nowrap mt-1 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <><i className="ri-loader-4-line animate-spin text-base"></i>처리 중...</>
                ) : (
                  "회원가입"
                )}
              </button>
            </form>
          )}
        </div>

        {/* Bottom link */}
        <p className="text-center text-gray-600 text-xs mt-5">
          {tab === "login" ? (
            <>
              아직 계정이 없으신가요?{" "}
              <button
                onClick={() => switchTab("signup")}
                className="text-violet-400 hover:text-violet-300 transition-colors cursor-pointer"
              >
                회원가입
              </button>
            </>
          ) : (
            <>
              이미 계정이 있으신가요?{" "}
              <button
                onClick={() => switchTab("login")}
                className="text-violet-400 hover:text-violet-300 transition-colors cursor-pointer"
              >
                로그인
              </button>
            </>
          )}
        </p>

        <p className="text-center text-gray-700 text-xs mt-3">
          <Link to="/" className="hover:text-gray-500 transition-colors cursor-pointer">
            ← 홈으로 돌아가기
          </Link>
        </p>
      </div>
    </div>
  );
}